import { RedirectToSignIn, useAuth, useUser } from '@clerk/react'
import type { ReactNode } from 'react'
import { getUserRole, type UserRole } from '../lib/auth'
import LoadingFallback from './loading-fallback'
import NotFound from './not-found'

interface ClientProtectedRouteProps {
  children: ReactNode
}

/**
 * Checks if the user has admin OR client role in their Clerk publicMetadata.
 * To grant client access, set publicMetadata.role = "client" in the Clerk dashboard.
 */
function isAdminOrClient(user: ReturnType<typeof useUser>['user']): boolean {
  if (!user) return false
  const role = getUserRole(user.publicMetadata as Record<string, unknown>)
  return role === 'admin' || role === 'client'
}

/**
 * Protected route that allows both admins and coaching clients.
 * Use this for client-only content that should also be accessible to admins.
 *
 * @example
 * <ClientProtectedRoute>
 *   <ClientDashboard />
 * </ClientProtectedRoute>
 */
export function ClientProtectedRoute({ children }: ClientProtectedRouteProps) {
  const { isLoaded: authLoaded, isSignedIn } = useAuth()
  const { isLoaded: userLoaded, user } = useUser()

  // Show loading state while checking authentication
  if (!authLoaded || !userLoaded) {
    return <LoadingFallback />
  }

  // Redirect to sign in if not authenticated
  if (!isSignedIn) {
    return <RedirectToSignIn />
  }

  // Show 404 if user is not an admin or client (don't reveal that these routes exist)
  if (!isAdminOrClient(user)) {
    return <NotFound />
  }

  return <>{children}</>
}

/**
 * Hook to check if the current user is a coaching client (or admin).
 * Useful for conditionally rendering client-only UI elements.
 *
 * @example
 * const { isClient, isLoaded } = useIsClient()
 * if (isLoaded && isClient) {
 *   // Show client-only features
 * }
 */
export function useIsClient(): {
  isClient: boolean
  isLoaded: boolean
  role: UserRole
} {
  const { isLoaded: userLoaded, user } = useUser()

  const role = user
    ? getUserRole(user.publicMetadata as Record<string, unknown>)
    : null

  return {
    isClient: role === 'admin' || role === 'client',
    isLoaded: userLoaded,
    role,
  }
}
